import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { AddressFields } from '@/components/AddressFields'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FieldGroup } from '@/components/ui/field'
import { parseError } from '@/lib/errors'
import { type SalesLead, useUpdateSalesLead } from '@/models/salesLead'

interface FormData {
  addressLine1: string
  city: string
  state: string
  zipCode: string
}

interface EditLeadAddressDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lead: SalesLead
}

export function EditLeadAddressDialog({ open, onOpenChange, lead }: EditLeadAddressDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit address</DialogTitle>
        </DialogHeader>
        <EditLeadAddressForm key={lead.id} lead={lead} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}

// Keyed on lead.id, mounted only while open: neither the 2s poll nor a lead switch can stale it.
function EditLeadAddressForm({ lead, onClose }: { lead: SalesLead; onClose: () => void }) {
  const updateMutation = useUpdateSalesLead()
  const { control, handleSubmit, setValue } = useForm<FormData>({
    defaultValues: {
      addressLine1: lead.addressLine1,
      city: lead.city,
      state: lead.state,
      zipCode: lead.zipCode,
    },
  })

  const onSubmit = (data: FormData) => {
    updateMutation.mutate(
      {
        id: lead.id,
        addressLine1: data.addressLine1.trim(),
        city: data.city.trim(),
        state: data.state,
        zipCode: data.zipCode.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Address updated')
          onClose()
        },
        onError: (error) => toast.error(parseError(error)),
      }
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FieldGroup className="mt-4">
        <AddressFields
          control={control}
          setValue={setValue}
          names={{ line1: 'addressLine1', city: 'city', state: 'state', zip: 'zipCode' }}
        />
      </FieldGroup>
      <DialogFooter className="mt-6">
        <Button type="button" variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button type="submit" loading={updateMutation.isPending ? 'Saving…' : undefined}>
          Save
        </Button>
      </DialogFooter>
    </form>
  )
}
